/**
 * `<SettlementReceipt>` — the `RESOLVED` end-state of `<ResolutionPanel>`
 * (`components.md` §2.6).
 *
 * ```
 *   ▲ UP  ·  RESOLVED                     btc-updown-1m-1786637400
 *   Settle  $63,441.28      Strike  $63,418.06      Δ  +$23.22
 *   print at 21:47:00
 *   Held    100 UP @ 0.63                 Payout      $100.000000
 *   Cost    $63.000000    Fee $0.000000   Net        +$37.000000
 * ```
 *
 * It does not appear from nowhere: it is what the resolution panel *becomes in
 * place*, in the secondary surface it already occupies, the moment the
 * settlement price is final. **The ticket is never involved at any point**, so
 * the user is not made to choose between reading their receipt and trading the
 * next market — a choice they would have been forced into sixty times an hour
 * (§F24, which dissolved that conflict rather than settling it).
 *
 * ## The receipt is a notification, not the record
 *
 * The distinction is load-bearing: a dismissed toast must never be the last
 * place a user could have learned whether they won. The durable answer is the
 * Settled tab, which renders the same store this does (§F24f).
 *
 * ## Every number here is the server's
 *
 * Especially `Net`. All six terms of the P&L identity ride on the frame —
 * `payout − cost_basis − fees_charged + rebates_credited + fees_refunded −
 * rebates_reversed` — precisely so a client can *check* it rather than compute
 * it, and §6.2 rule 2 says never to derive money locally: the fee was applied at
 * fill time, and the moment the rate stops being zero, re-deriving it here
 * becomes a support ticket that says we stole money. `settlementIdentity()`
 * below is the check, in `bigint`, and a unit test is its caller.
 */

import { memo } from 'react'
import { Glyph, NumCell, Pill } from '@/components/primitives'
import { cx } from '@/lib/cx'
import {
  avgEntryCents,
  formatAvgCents,
  formatPriceString,
  formatShares,
  formatSignedUsdc,
  formatUsdc,
} from '@/lib/format'
import { parseFixed, USDC_DP } from '@/lib/decimal'
import { observationSentence } from '@/domain/resolution'

/** `openapi.Settlement` / `asyncapi.SettlementData` — one object, two
 *  transports, reconciled in §F14 and checked by `npm run gen`. */
export type Settlement = Record<string, unknown>

export interface SettlementReceiptProps {
  settlement: Settlement
  /**
   * `panel` holds the secondary surface for ~1s; `toast` is the 6-second
   * notification it animates down into. Same numbers, two densities — never a
   * summary, because the toast is where most users read it.
   */
  variant?: 'panel' | 'toast'
  className?: string
}

export const SettlementReceipt = memo(function SettlementReceipt({
  settlement,
  variant = 'panel',
  className,
}: SettlementReceiptProps) {
  const outcome = stringOf(settlement, 'outcome')
  const mine = stringOf(settlement, 'my_outcome')
  const void_ = outcome === 'VOID'
  const won = settlement.won === true
  const degraded = settlement.degraded === true
  const marketId = stringOf(settlement, 'market_id') ?? ''

  const netPnl = stringOf(settlement, 'net_pnl') ?? '0.000000'
  // `null` for a losing position (nothing to redeem), until the transaction is
  // mined, and throughout on a venue configured without a chain. All three are
  // ordinary, so the row is simply absent rather than showing a placeholder.
  const txHash = stringOf(settlement, 'redemption_tx_hash')
  const negative = netPnl.trimStart().startsWith('-')
  const tone = void_ ? 'neutral' : won ? 'up' : 'down'

  const held = void_ || !won ? stringOf(settlement, 'shares_lost') : stringOf(settlement, 'shares_won')
  const costBasis = stringOf(settlement, 'cost_basis') ?? '0.000000'
  const avg = held ? avgEntryCents(costBasis, held) : null

  const price = settlement.settlement_price as Record<string, unknown> | null
  const strike = settlement.strike_price as Record<string, unknown> | null
  const windowEndMs = numberOf(price, 'window_end_ms')

  return (
    <div
      data-testid="settlement-receipt"
      data-market={marketId}
      data-outcome={outcome ?? ''}
      className={cx('flex flex-col gap-1_5', variant === 'panel' && 'p-3', className)}
    >
      <div className="flex items-center justify-between gap-2">
        <Pill tone={tone}>
          {void_ ? (
            'VOIDED'
          ) : (
            <>
              <Glyph name={outcome === 'DOWN' ? 'down' : 'up'} label={null} /> {outcome ?? 'UP'} · RESOLVED
            </>
          )}
        </Pill>
        {degraded ? <Pill tone="warning">DEGRADED</Pill> : null}
        <span className="ud-num ml-auto truncate text-micro text-text-tertiary" title={marketId}>
          {marketId}
        </span>
      </div>

      {/* A void must state the *money* outcome in the first line
          (`components.md` §2.8) — not the mechanism, and not an apology. */}
      {void_ ? (
        <span className="text-body-sm text-text-secondary">
          voided — every order refunded at entry cost
        </span>
      ) : (
        <>
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-0_5">
            <Figure label="Settle" value={priceOf(price)} />
            <Figure label="Strike" value={priceOf(strike)} />
            <Figure
              label="Δ"
              value={stringOf(settlement, 'delta') ? formatPriceString(stringOf(settlement, 'delta') as string) : '—'}
              tone={stringOf(settlement, 'delta')?.startsWith('-') ? 'down' : 'up'}
            />
          </div>
          {/* `docs/07` §4: the instant the price was printed, read off the
              settlement's own `window_end_ms` — never written (§F74). */}
          {windowEndMs !== null ? (
            <span className="text-caption text-text-tertiary">{observationSentence(windowEndMs)}</span>
          ) : null}
        </>
      )}

      <div className="grid grid-cols-2 gap-x-4 gap-y-0_5 border-t border-border-subtle pt-1_5">
        <Figure
          label="Held"
          value={
            held ?
              `${formatShares(held)}${mine ? ` ${mine}` : ''}${avg ? ` @ ${formatAvgCents(avg)}` : ''}`
            : '—'
          }
        />
        <Figure label="Payout" value={formatUsdc(stringOf(settlement, 'payout') ?? '0.000000')} align="right" />
        <Figure label="Cost" value={formatUsdc(costBasis)} />
        {/* Always rendered, at zero (`components.md` §2.4). Not a badge, not a
            promotion — a fact, and its value comes from the server rather than
            from a client constant, including not from a hardcoded zero. */}
        <Figure label="Fee" value={formatUsdc(stringOf(settlement, 'fees_charged') ?? '0.000000')} align="right" />
        <Figure
          label="Net"
          align="right"
          className="col-span-2"
          tone={negative ? 'down' : 'up'}
          value={formatSignedUsdc(netPnl)}
          flash
        />
      </div>

      {txHash === null ? null : (
        <div className="border-t border-border-subtle pt-1_5">
          {/*
            **The proof, not decoration.** Phase 8's exit criterion 1 is that the
            hash shown here resolves to a transaction which moved this user's
            collateral — so it is rendered as the settling transaction rather
            than as a technical footnote.

            Truncated in the middle because a 66-character hash is unreadable in
            full and unrecognisable if only its head is shown; the tail is what
            distinguishes two hashes at a glance. The full value stays in the
            title, so it can still be copied.

            No explorer link: `config.chain.explorer_tx_url_template` is the
            source for one, and the venue does not publish it yet. A hardcoded
            arbiscan URL would be a constant that happens to equal a payload
            field, which is the shape this project has been caught by three times
            (invariant 11).
          */}
          <Figure label="Settled on-chain" value={truncateHash(txHash)} title={txHash} />
        </div>
      )}
    </div>
  )
})

/** `0x1234…cdef` — head and tail, because the tail is what distinguishes two. */
function truncateHash(hash: string): string {
  return hash.length <= 18 ? hash : `${hash.slice(0, 10)}\u2026${hash.slice(-8)}`
}

// ---------------------------------------------------------------------------

function Figure({
  label,
  value,
  align = 'left',
  tone,
  className,
  title,
  flash = false,
}: {
  label: string
  value: string
  align?: 'left' | 'right'
  tone?: 'up' | 'down'
  className?: string
  /** Hover text — the untruncated value, where the rendered one is shortened. */
  title?: string
  flash?: boolean
}) {
  return (
    <span
      className={cx(
        'flex items-baseline gap-1_5',
        align === 'right' && 'justify-end',
        className,
      )}
      title={title}
    >
      <span className="text-micro text-text-tertiary">{label}</span>
      <NumCell
        align={align}
        flash={flash}
        value={value}
        className={cx(
          'text-body-sm',
          tone === 'up' ? 'text-up-text'
          : tone === 'down' ? 'text-down-text'
          : 'text-text-primary',
        )}
      />
    </span>
  )
}

function priceOf(source: Record<string, unknown> | null): string {
  const value = source?.value
  return typeof value === 'string' ? formatPriceString(value) : '—'
}

function stringOf(source: Record<string, unknown> | null, field: string): string | null {
  const value = source?.[field]
  return typeof value === 'string' ? value : null
}

function numberOf(source: Record<string, unknown> | null, field: string): number | null {
  const value = source?.[field]
  return typeof value === 'number' ? value : null
}

// ---------------------------------------------------------------------------

export interface SettlementIdentity {
  /** `payout − cost_basis − fees_charged + rebates_credited + fees_refunded − rebates_reversed` */
  computedMicro: bigint
  reportedMicro: bigint
  ok: boolean
}

/**
 * The P&L identity, in integer minor units.
 *
 * Not used to *render* anything — §6.2 rule 2 is absolute about that — but the
 * six terms are on the frame specifically so it is checkable, and a receipt
 * whose `Net` does not close against its own `Cost`, `Fee` and `Payout` is a
 * receipt that will be screenshotted. On a void it closes to exactly zero:
 * `payout` is the cost basis, `fees_refunded` is `fees_charged`, and
 * `rebates_reversed` is `rebates_credited` — §8.3f's "nobody wins, nobody
 * loses", made assertable instead of asserted.
 */
export function settlementIdentity(settlement: Settlement): SettlementIdentity {
  const micro = (field: string): bigint => {
    const value = settlement[field]
    if (typeof value !== 'string') return 0n
    try {
      return parseFixed(value, USDC_DP)
    } catch {
      return 0n
    }
  }
  const computedMicro =
    micro('payout') -
    micro('cost_basis') -
    micro('fees_charged') +
    micro('rebates_credited') +
    micro('fees_refunded') -
    micro('rebates_reversed')
  const reportedMicro = micro('net_pnl')
  return { computedMicro, reportedMicro, ok: computedMicro === reportedMicro }
}

/** `|net_pnl|`, for the two-receipt ordering of §2.6 — biggest move first,
 *  never merged into a summary. Each market resolved on its own window. */
export function absNetPnlMicro(settlement: Settlement): bigint {
  const value = settlement.net_pnl
  if (typeof value !== 'string') return 0n
  try {
    const micro = parseFixed(value, USDC_DP)
    return micro < 0n ? -micro : micro
  } catch {
    return 0n
  }
}
